import type { ChatMessage } from '../context/ChatContext';

type StoredChatMessage = Omit<ChatMessage, 'content'> & {
  content?: string;
};

const removeTransientConversationSuggestionMetadata = (
  message: ChatMessage,
): ChatMessage => {
  if (!message.metadata || !('conversationSuggestionsStatus' in message.metadata)) {
    return message;
  }

  const { conversationSuggestionsStatus: _conversationSuggestionsStatus, ...metadata } = message.metadata;
  return {
    ...message,
    metadata,
  };
};

// Streaming assistant messages keep their content as ordered text segments so
// sessionStorage can avoid saving a second concatenated copy of the same text.
export function getMessageSegments(metadata?: Record<string, any>): string[] {
  if (!Array.isArray(metadata?.messageSegments)) {
    return [];
  }

  return metadata.messageSegments.filter((segment: unknown): segment is string => (
    typeof segment === 'string' && segment.length > 0
  ));
}

// Restored chat state still needs the normal concatenated content field because
// the message renderer, PDF export, and markdown pipeline read from it at runtime.
export function buildContentFromMessageSegments(metadata?: Record<string, any>): string | null {
  const messageSegments = getMessageSegments(metadata);
  return messageSegments.length > 0 ? messageSegments.join('') : null;
}

// Persist segmented assistant messages without duplicating their full content;
// plain messages keep content unchanged for backward compatibility.
export function prepareMessagesForSessionStorage(messages: ChatMessage[]): StoredChatMessage[] {
  return messages.map((originalMessage) => {
    const message = removeTransientConversationSuggestionMetadata(originalMessage);
    const segmentContent = buildContentFromMessageSegments(message.metadata);
    if (segmentContent === null) {
      return message;
    }

    return {
      ...message,
      content: '',
    };
  });
}

// Accepts unknown JSON from sessionStorage and normalizes it back to the runtime
// ChatMessage shape expected by the rest of the chat UI.
export function restoreMessagesFromSessionStorage(storedMessages: unknown): ChatMessage[] {
  if (!Array.isArray(storedMessages)) {
    return [];
  }

  return storedMessages.map((storedMessage) => {
    const message = storedMessage as StoredChatMessage;
    const segmentContent = buildContentFromMessageSegments(message.metadata);

    return {
      ...message,
      content: segmentContent ?? message.content ?? '',
    };
  });
}

// Live streamed messages include pre-tool narration in content. For PDF export,
// match persisted history/admin behavior by exporting the final post-tool segment.
export function getAssistantPdfExportContent(
  content: string,
  metadata?: Record<string, any>,
): string {
  if (typeof metadata?.pdfExportContent === 'string' && metadata.pdfExportContent.trim()) {
    return metadata.pdfExportContent;
  }

  const streamEvents = Array.isArray(metadata?.streamEvents)
    ? metadata.streamEvents
    : [];
  const usedTools = streamEvents.some((event) => (
    event?.type === 'tool_use' || event?.type === 'tool_call'
  ));

  if (!usedTools) {
    return content;
  }

  const messageSegments = getMessageSegments(metadata).filter((segment) => segment.trim().length > 0);
  const finalSegment = messageSegments[messageSegments.length - 1];

  return finalSegment?.trimStart() || content;
}
